/**
 * All requests go through the relative `/api` path: Vite proxies it in development,
 * the reverse proxy in production. No absolute URLs anywhere in the frontend.
 */

export interface Project {
  jiraId: string;
  key: string;
  name: string;
  /** Jira's category, `Active` / `Inactive`. A marker of metadata quality. */
  categoryName: string | null;
  isActiveCategory: boolean;
}

export interface User {
  userKey: string;
  displayName: string;
  active: boolean;
}

/** Часы сотрудника на одной команде: команда — свойство задачи, не человека. */
export interface HoursRow {
  authorKey: string;
  displayName: string;
  /** Значение поля Team; пустая строка — команда не заполнена. */
  team: string;
  /** Последнее звено пути команды, для шапки колонки. */
  label: string;
  seconds: number;
}

export interface Team {
  team: string;
  label: string;
  /** Часы за выбранный период; ноль — в этом месяце команда не работала. */
  seconds: number;
  /** Часы за всю историю — ими объясняется, почему команда вообще в списке. */
  totalSeconds: number;
}

/** Кого показывать строками матрицы помимо тех, у кого есть часы за период. */
export type EmployeeScope = 'none' | 'logging' | 'active';

export interface EmployeeRow {
  authorKey: string;
  displayName: string;
  active: boolean;
  /** Часы за период; ноль — это тоже ответ, ради него строка и показана. */
  seconds: number;
}

export interface HoursResponse {
  period: { from: string; to: string };
  employees: EmployeeRow[];
  rows: HoursRow[];
  totals: {
    seconds: number;
    byTeam: Record<string, number>;
    byAuthor: Record<string, number>;
  };
}

/** One task inside a matrix cell: what the hours were actually spent on. */
export interface IssueHoursRow {
  authorKey: string;
  displayName: string;
  issueId: string;
  issueKey: string;
  summary: string;
  issueType: string | null;
  status: string | null;
  team: string | null;
  projectId: string;
  projectKey: string;
  projectName: string;
  categoryName: string | null;
  entries: number;
  seconds: number;
  lastStartedAt: string;
  /** Built by the API: the frontend never knows the Jira host. */
  url: string | null;
}

/** Hours grouped by team and project: who the work was for. */
export interface TeamHoursRow {
  team: string | null;
  projectId: string;
  projectKey: string;
  projectName: string;
  issues: number;
  seconds: number;
}

export interface IssueHoursResponse {
  period: { from: string; to: string };
  rows: IssueHoursRow[];
  /** Covers the whole period even when `rows` was cut by the limit. */
  byTeam: TeamHoursRow[];
  totals: { seconds: number; issues: number };
  truncated: boolean;
}

export interface SyncStatus {
  watermark: string | null;
  /** True while a sync is in progress — the button shows it instead of pretending. */
  running: boolean;
  runs: {
    entity: string;
    status: string;
    startedAt: string;
    finishedAt: string | null;
    itemsRead: number;
    itemsWritten: number;
    error: string | null;
  }[];
}

export interface HoursParams {
  from: string;
  to: string;
  teams: string[];
  authorKeys: string[];
  employees?: EmployeeScope;
}

/** Surfaces the API's error shape instead of a bare "Failed to fetch". */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Ошибка → строка для экрана, одним правилом на всё приложение.
 *
 * Раньше эта функция была скопирована дословно в двух компонентах, а ещё три
 * писали свой вариант через `instanceof ApiError ? … : 'не удалось загрузить'`.
 * Разные тексты на одну и ту же беду читаются как разные беды.
 *
 * Код статуса в скобках — не для человека, а для того, кому он скажет: 403 и 502
 * требуют разного, и без цифры это выясняется через полчаса переписки.
 */
export function describeError(cause: unknown): string {
  if (cause instanceof ApiError) return `${cause.message} (${cause.status})`;
  if (cause instanceof Error) return cause.message;
  return 'неизвестная ошибка';
}

/**
 * Сессия кончилась — приложению пора показать вход.
 *
 * Событие, а не проброс колбэка через все запросы: 401 может прийти на любой
 * из полутора десятков вызовов, и обрабатывать его в каждом значит однажды
 * забыть про один и оставить человека перед пустым экраном.
 */
export const UNAUTHENTICATED_EVENT = 'cs:unauthenticated';

async function request<T>(path: string, signal?: AbortSignal, method = 'GET'): Promise<T> {
  const response = await fetch(`/api${path}`, { signal, method });

  if (response.status === 401) window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: { message?: string } }).error.message ?? response.statusText)
        : response.statusText;
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<T>;
}

/** Роли: смотрит / ведёт сверку / администрирует. Порядок — по старшинству. */
export type Role = 'viewer' | 'reconciler' | 'admin';

export interface Me {
  login: string;
  displayName: string | null;
  role: Role;
  /** Чем подтверждена личность: локальный список или LDAP. Видно на экране входа. */
  provider: string;
}

const RANK: Record<Role, number> = { viewer: 0, reconciler: 1, admin: 2 };

/** Старшинство ролей: `admin` умеет всё, что `reconciler`. То же правило, что на сервере. */
export function roleAllows(role: Role | undefined, required: Role): boolean {
  return role !== undefined && RANK[role] >= RANK[required];
}

/**
 * Кто вошёл. 401 здесь — обычный ответ, а не сбой: так приложение узнаёт,
 * что нужно показать вход.
 */
export async function fetchMe(signal?: AbortSignal): Promise<Me | null> {
  const response = await fetch('/api/auth/me', { signal });
  if (response.status === 401) return null;
  if (!response.ok) throw new ApiError('Не удалось проверить сессию', response.status);
  return response.json() as Promise<Me>;
}

export async function login(loginValue: string, password: string): Promise<Me> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ login: loginValue, password }),
  });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: { message?: string } }).error.message ?? response.statusText)
        : response.statusText;
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<Me>;
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' });
}

export function fetchProjects(signal?: AbortSignal): Promise<Project[]> {
  return request<Project[]>('/projects', signal);
}

export function fetchUsers(signal?: AbortSignal): Promise<User[]> {
  return request<User[]>('/users', signal);
}

/** Команды, на которые списывали часы **за период**, — для фильтра и колонок. */
export function fetchTeams(period: Period, signal?: AbortSignal): Promise<Team[]> {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  return request<Team[]>(`/teams?${query.toString()}`, signal);
}

export function fetchSyncStatus(signal?: AbortSignal): Promise<SyncStatus> {
  return request<SyncStatus>('/sync/status', signal);
}

export type RefreshOutcome =
  | { status: 'started' }
  | { status: 'already_running'; startedAt: string }
  | { status: 'too_soon'; retryAfterSeconds: number };

/** Asks the server to pull fresh hours from Jira. Returns at once; progress is in the status. */
export function refreshSync(signal?: AbortSignal): Promise<RefreshOutcome> {
  return request<RefreshOutcome>('/sync/refresh', signal, 'POST');
}

/** Листы выгрузки; первый — то, что нужно чаще всего. */
export const CSV_SHEETS = ['Матрица', 'Часы', 'Задачи', 'Команды', 'Сотрудники'] as const;

export type CsvSheet = (typeof CSV_SHEETS)[number];

/**
 * Ссылка на выгрузку. Именно ссылка, а не fetch: скачиванием файла занимается
 * браузер, ему для этого не нужен ни blob в памяти, ни наш код.
 *
 * Передаётся только период: выгрузка идёт за выбранный срок, но по всем
 * проектам и сотрудникам.
 */
export function csvUrl(sheet: CsvSheet, period: Period): string {
  const query = new URLSearchParams({ from: period.from, to: period.to, sheet });
  return `/api/export/csv?${query.toString()}`;
}

/** Период выгрузки — тот же, что и на экране. */
export interface Period {
  from: string;
  to: string;
}

function hoursQuery(params: HoursParams): URLSearchParams {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  // Пустая строка — «без команды», её тоже надо передать: `append` с пустым
  // значением даёт `teams=`, и сервер читает это как значение, а не как отсутствие.
  for (const team of params.teams) query.append('teams', team);
  for (const key of params.authorKeys) query.append('authorKeys', key);
  if (params.employees) query.set('employees', params.employees);
  return query;
}

export function fetchHours(params: HoursParams, signal?: AbortSignal): Promise<HoursResponse> {
  return request<HoursResponse>(`/hours?${hoursQuery(params).toString()}`, signal);
}

export function fetchIssueHours(
  params: HoursParams,
  signal?: AbortSignal,
): Promise<IssueHoursResponse> {
  return request<IssueHoursResponse>(`/hours/by-issue?${hoursQuery(params).toString()}`, signal);
}

/** Цвет строки в ревизии. Правило считает сервер — оно одно на экран и на выгрузку. */
export type Severity = 'red' | 'orange' | 'yellow' | 'none';

export interface RevisionIssueRow {
  issueKey: string;
  summary: string;
  projectId: string;
  projectKey: string;
  projectName: string;
  status: string | null;
  issueType: string | null;
  team: string | null;
  assigneeKey: string | null;
  assigneeName: string | null;
  assigneeActive: boolean | null;
  reporterName: string | null;
  reporterActive: boolean | null;
  /** Часы с самой задачи в Jira: они старше нашего окна worklog. */
  seconds: number;
  remainingSeconds: number;
  /** Сколько из них есть у нас в worklog — обычно меньше, и это нормально. */
  ourSeconds: number;
  updatedAt: string | null;
  staleDays: number | null;
  severity: Severity;
  url: string | null;
}

export interface RevisionProjectRow {
  projectId: string;
  projectKey: string;
  projectName: string;
  issues: number;
  seconds: number;
}

export interface RevisionIssuesResponse {
  rows: RevisionIssueRow[];
  totals: { severity: Severity; issues: number; seconds: number }[];
  /** Проекты по всему найденному, а не по показанной тысяче строк. */
  projects: RevisionProjectRow[];
  shown: number;
  found: number;
  truncated: boolean;
}

export type PersonCohort = 'logging' | 'stopped' | 'never' | 'left';

export interface RevisionPersonRow {
  userKey: string;
  displayName: string;
  email: string | null;
  active: boolean;
  seconds: number;
  lastWorklogAt: string | null;
  silentDays: number | null;
  months: number;
  openAssigned: number;
  openAssignedSeconds: number;
  openReported: number;
  cohort: PersonCohort;
}

export interface RevisionPeopleResponse {
  rows: RevisionPersonRow[];
  totals: Record<PersonCohort, number>;
}

export function fetchRevisionIssues(
  params: { projectIds: string[]; severity?: Severity },
  signal?: AbortSignal,
): Promise<RevisionIssuesResponse> {
  const query = new URLSearchParams();
  for (const id of params.projectIds) query.append('projectIds', id);
  if (params.severity) query.set('severity', params.severity);
  const suffix = query.toString();
  return request<RevisionIssuesResponse>(`/revision/issues${suffix ? `?${suffix}` : ''}`, signal);
}

export function fetchRevisionPeople(signal?: AbortSignal): Promise<RevisionPeopleResponse> {
  return request<RevisionPeopleResponse>('/revision/people', signal);
}

/** Подписи корзин — те же, что в файле и на карточках. */
export const SEVERITY_LABELS: Record<Severity, string> = {
  red: 'Часы повисли',
  orange: 'Некому вести',
  yellow: 'Стоит',
  none: 'В работе',
};

/**
 * Ссылка на выгрузку ревизии. Периода нет: список не привязан к сроку отчёта.
 * Фильтр по проекту передаётся, чтобы файл совпадал с тем, что человек видит.
 */
export function revisionCsvUrl(params: { projectIds: string[]; severity?: Severity }): string {
  const query = new URLSearchParams();
  for (const id of params.projectIds) query.append('projectIds', id);
  if (params.severity) query.set('severity', params.severity);
  const suffix = query.toString();
  return `/api/export/revision.csv${suffix ? `?${suffix}` : ''}`;
}

// --- Сверка с PeopleForce ----------------------------------------------------

export interface HrWarning {
  kind: string;
  message: string;
  row?: number;
  personId?: string;
}

export interface HrPreviewPerson {
  personId: string;
  fullName: string;
  department: string | null;
  employmentType: string | null;
  seconds: number;
  vacationDays: number;
  sickDays: number;
  holidayDays: number;
  hiredAt: string | null;
  leftAt: string | null;
  match: { userKey: string; displayName: string } | null;
  suggestions: { userKey: string; displayName: string; score: number }[];
}

export interface HrPreview {
  period: { from: string; to: string; year: number; month: number } | null;
  detection: { evidence: string[]; confident: boolean };
  totals: {
    people: number;
    peopleWithoutHours: number;
    seconds: number;
    hours: number;
    vacationDays: number;
    sickDays: number;
    holidayDays: number;
    hiredInPeriod: number;
    leftInPeriod: number;
  };
  warnings: HrWarning[];
  people: HrPreviewPerson[];
  saved: { importId: number; people: number; days: number; replacedDays: number } | null;
}

export interface HrPeriodRow {
  from: string;
  to: string;
  people: number;
  hours: number;
  /** Сколько раз месяц загружали: данные при этом всегда от последней загрузки. */
  imports: number;
  lastUploadedAt: string;
  lastFileName: string;
  /** Сохранён ли исходный файл: у месяцев, загруженных до 2026-09-07, его нет. */
  hasSource: boolean;
  /**
   * Приложена ли к месяцу выгрузка состава команд. Нет — весь остаток месяца
   * ушёл в базовую команду: так считаются все месяцы до сентября 2026.
   */
  hasTeams: boolean;
  /** Последний заполненный день табеля; раньше конца месяца — его ещё ведут. */
  filledTo: string | null;
}

export interface HrComparisonRow {
  personId: string;
  fullName: string;
  /** Должность из PeopleForce. */
  position: string | null;
  department: string | null;
  /**
   * Команда из выгрузки состава этого месяца — та, в которую ушёл его остаток.
   * `null`: человека нет ни в одной команде месяца либо файла состава не было.
   */
  pfTeam: string | null;
  employmentType: string | null;
  leftAt: string | null;
  userKey: string | null;
  jiraName: string | null;
  /** Почта из связанной учётки Jira; в выгрузке PeopleForce её нет. */
  jiraEmail: string | null;
  linkSource: string | null;
  hrSeconds: number;
  /** Часы, не засчитанные как рабочие: full-time в выходной или праздник. */
  excludedSeconds: number;
  jiraSeconds: number;
  diffSeconds: number;
  vacationDays: number;
  sickDays: number;
  holidayDays: number;
  /** Дни отсутствия: отпуск, больничный, праздник, отгул — только те, в которые не работали. */
  absenceDays: number;
  /** Оценка часов, не отработанных по документальной причине. */
  absenceSeconds: number;
  /** Обычный день сотрудника, из которого считается оценка. */
  typicalDaySeconds: number;
  /** Часы Jira по колонкам: ключ — id проекта или значение команды. */
  byColumn: Record<string, number>;
  /**
   * Колонка команды, к которой сервер прибавил его остаток (PF − Jira): команда
   * его подразделения. Сопоставление живёт на сервере, экран только рисует.
   */
  generalColumn: string | null;
  /** Его часы по сервисным командам: сервер уже сложил их в базовую колонку. */
  serviceSeconds: number;
  /** Куда сложил. `null` — сервисных часов у него нет. */
  serviceColumn: string | null;
  /** `false` — человек нашёлся только в Jira, в выгрузке PeopleForce его нет. */
  inHr: boolean;
}

/** Колонка матрицы: проект Jira или команда с задачи — смотря какой разрез. */
export interface HrColumn {
  id: string;
  key: string;
  name: string;
  /** Часы Jira в колонке. */
  seconds: number;
  /** Остаток PF − Jira, прибавленный к колонке: ненулевой у команды подразделения. */
  generalSeconds: number;
  /** Часы сервисных команд, сложенные в эту колонку: ненулевые только у базовой. */
  serviceSeconds: number;
}

/** Что стоит в колонках: проект задачи или её поле Team. */
export type HrDimension = 'project' | 'team';

/** Списывал часы, но в табеле его нет: вне периметра расчёта. */
export interface HrOutsider {
  userKey: string;
  displayName: string;
  seconds: number;
}

export interface HrComparison {
  period: { from: string; to: string };
  /** Последний заполненный день табеля; раньше конца периода — месяц ещё ведут. */
  filledTo: string | null;
  dimension: HrDimension;
  /** Команды с часами за период, самая большая первой (`dimension` решает, чем они наполнены). */
  columns: HrColumn[];
  /** Команды состава, которых нет среди команд Jira: колонка заведена по названию из файла. */
  unmatchedTeams: string[];
  /** Сервисные команды периода: своих колонок у них нет, часы ушли в базовую. */
  serviceTeams: { name: string; seconds: number }[];
  rows: HrComparisonRow[];
  /** Не в таблице и не в итогах: их компания считается по другому табелю. */
  outsiders: HrOutsider[];
  totals: {
    people: number;
    linked: number;
    unlinked: number;
    jiraOnly: number;
    outsideSeconds: number;
    hrSeconds: number;
    absenceSeconds: number;
    jiraSeconds: number;
    silent: number;
    /** Сколько часов всего уехало из сервисных команд в базовую. */
    serviceSeconds: number;
  };
}

export interface HrPersonRow {
  personId: string;
  fullName: string;
  department: string | null;
  userKey: string | null;
  jiraName: string | null;
  linkSource: string | null;
  suggestions: { userKey: string; displayName: string; score: number }[];
}

/**
 * Загрузка файла. Отправляем **байты**, а не текст: декодировать должен сервер,
 * иначе выгрузка в другой кодировке молча превратится в кракозябры.
 */
export async function uploadHrFile(
  file: File,
  options: { dryRun: boolean; year?: number; month?: number },
): Promise<HrPreview> {
  const query = new URLSearchParams({ fileName: file.name });
  if (options.dryRun) query.set('dryRun', 'true');
  if (options.year && options.month) {
    query.set('year', String(options.year));
    query.set('month', String(options.month));
  }

  const response = await fetch(`/api/hr/import?${query.toString()}`, {
    method: 'POST',
    headers: { 'content-type': 'text/csv' },
    body: await file.arrayBuffer(),
  });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: { message?: string } }).error.message ?? response.statusText)
        : response.statusText;
    throw new ApiError(message, response.status);
  }
  return response.json() as Promise<HrPreview>;
}

/** Состав команд месяца: что записал последний загруженный файл. */
export interface HrTeamsStatus {
  fileName: string;
  hasSource: boolean;
  teams: number;
  people: number;
  /** Из них нашлись в табеле месяца. */
  matched: number;
  /** Кого из файла в табеле нет: в расчёте они не участвуют. */
  unmatched: { name: string; team: string }[];
  uploadedAt: string;
  uploadedBy: string | null;
}

/** Ответ на загрузку выгрузки состава: что в файле и как он лёг на табель. */
export interface HrTeamsUpload {
  period: { from: string; to: string; year: number; month: number } | null;
  fileName: string;
  totals: { teams: number; people: number; matched: number };
  teams: {
    name: string;
    manager: string | null;
    people: number;
    matched: number;
    /** `null` — такой команды в Jira нет, колонка заведётся по названию из файла. */
    jiraTeam: string | null;
  }[];
  unmatched: { name: string; team: string }[];
  unknownTeams: string[];
  warnings: HrWarning[];
  conflicts: { name: string; teams: string[] }[];
  saved: { members: number; replacedMembers: number } | null;
}

/**
 * Загрузка выгрузки состава команд — тем же способом, что и табель: байтами,
 * к явно выбранному месяцу.
 */
export async function uploadHrTeams(
  file: File,
  options: { year: number; month: number },
): Promise<HrTeamsUpload> {
  const query = new URLSearchParams({
    fileName: file.name,
    year: String(options.year),
    month: String(options.month),
  });

  const response = await fetch(`/api/hr/teams?${query.toString()}`, {
    method: 'POST',
    headers: { 'content-type': 'text/csv' },
    body: await file.arrayBuffer(),
  });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: { message?: string } }).error.message ?? response.statusText)
        : response.statusText;
    throw new ApiError(message, response.status);
  }
  return response.json() as Promise<HrTeamsUpload>;
}

/** Состав команд месяца. `null` — выгрузку к месяцу не прикладывали. */
export async function fetchHrTeams(
  period: { from: string; to: string },
  signal?: AbortSignal,
): Promise<HrTeamsStatus | null> {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  const body = await request<{ teams: HrTeamsStatus | null }>(
    `/hr/teams?${query.toString()}`,
    signal,
  );
  return body.teams;
}

// Журнал загрузок с экрана убран (решение 2026-09-07), клиента к нему больше нет.
// Маршрут `/api/hr/imports` жив: это аудит — кто и когда положил файл.

/** Кандидат на напоминание: работающая учётка Jira и её часы за месяц. */
export interface ReminderCandidate {
  userKey: string;
  displayName: string;
  /** Без почты письмо не составить — такие строки экран показывает, но выбрать не даёт. */
  email: string | null;
  jiraSeconds: number;
  /** `null` — человека нет в табеле месяца: часы PeopleForce неизвестны. */
  hrSeconds: number | null;
  remainderSeconds: number | null;
  /** Когда этому человеку последний раз писали за этот месяц. */
  remindedAt: string | null;
  reminders: number;
}

/** Строка журнала: кому и когда открыли письмо. */
export interface ReminderLogRow {
  id: number;
  userKey: string;
  displayName: string | null;
  email: string;
  channel: string;
  status: string;
  subject: string;
  createdAt: string;
  createdBy: string | null;
}

export function fetchReminderCandidates(
  period: Period,
  signal?: AbortSignal,
): Promise<{
  period: { from: string; to: string };
  people: ReminderCandidate[];
  /** Люди табеля без связки с учёткой Jira: писать им нечем. */
  unlinked: string[];
}> {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  return request(`/reminders/candidates?${query.toString()}`, signal);
}

export function fetchReminderLog(period: Period, signal?: AbortSignal): Promise<ReminderLogRow[]> {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  return request(`/reminders?${query.toString()}`, signal);
}

/**
 * Запись писем в журнал — **после** того, как окно Gmail открыто.
 * Сервер почту не отправляет: записывается то, что произошло, а не намерение.
 */
export async function logReminders(
  period: Period,
  entries: { userKey: string; email: string; subject: string; body: string }[],
): Promise<{ written: number }> {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  const response = await fetch(`/api/reminders?${query.toString()}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ channel: 'gmail', entries }),
  });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: { message?: string } }).error.message ?? response.statusText)
        : response.statusText;
    throw new ApiError(message, response.status);
  }
  return response.json() as Promise<{ written: number }>;
}

/** Месяцы с данными — по одному на месяц, а не по одному на загрузку. */
export function fetchHrPeriods(signal?: AbortSignal): Promise<HrPeriodRow[]> {
  return request<HrPeriodRow[]>('/hr/periods', signal);
}

export function fetchHrComparison(
  period: Period,
  dimension: HrDimension,
  signal?: AbortSignal,
): Promise<HrComparison> {
  const query = new URLSearchParams({ from: period.from, to: period.to, dimension });
  return request<HrComparison>(`/hr/comparison?${query.toString()}`, signal);
}

/** День табеля: часы и сырая пометка, как в файле PeopleForce. */
export interface HrDay {
  date: string;
  seconds: number;
  marker: string | null;
  markerKind: string | null;
}

/**
 * Дни одного человека за месяц — его строка из исходного табеля.
 *
 * Отвечает на вопрос, который возникает прямо у числа: «почему у него 92 часа».
 */
export function fetchHrDays(
  period: Period,
  personId: string,
  signal?: AbortSignal,
): Promise<{ personId: string; days: HrDay[] }> {
  const query = new URLSearchParams({ from: period.from, to: period.to, personId });
  return request<{ personId: string; days: HrDay[] }>(`/hr/days?${query.toString()}`, signal);
}

/** Исходный файл PeopleForce, разобранный на строки для показа на экране. */
export interface HrSource {
  fileName: string;
  uploadedAt: string;
  header: string[];
  rows: string[][];
}

/**
 * Исходник месяца — строками, а не файлом на скачивание.
 *
 * Разобранные дни на экране это наше прочтение файла; на спор «в файле было
 * другое» отвечает только оригинал, и смотреть его надо там же, где сверяют,
 * а не в скачанном CSV, который ещё нужно чем-то открыть.
 */
export function fetchHrSource(period: Period, signal?: AbortSignal): Promise<HrSource> {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  return request<HrSource>(`/hr/source?${query.toString()}`, signal);
}

/**
 * Тот же исходник файлом: приложить к письму, открыть в Excel, отдать
 * бухгалтеру. Просмотр на экране отвечает на другой вопрос — «что в файле».
 */
export function hrSourceFileUrl(period: Period): string {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  return `/api/hr/source.csv?${query.toString()}`;
}

/** Ссылка на исходник выгрузки состава команд этого месяца. */
export function hrTeamsFileUrl(period: Period): string {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  return `/api/hr/teams.csv?${query.toString()}`;
}

/**
 * Ссылка на выгрузку сверки — за тот же период и разрез, что на экране.
 *
 * Поиск по фамилии не передаётся: он способ разглядеть строку, а не сузить
 * отчёт, и файл на пять строк вместо полусотни выглядел бы как полная сверка.
 *
 * Только XLSX: в сверке половина смысла в оформлении — закреплённая шапка,
 * цвет колонок, ширина под содержимое. CSV этого не несёт вовсе, и держать
 * вторую кнопку ради голых чисел незачем (решение 2026-09-04).
 */
export function hrExportUrl(period: Period, dimension: HrDimension): string {
  const query = new URLSearchParams({ from: period.from, to: period.to, dimension });
  return `/api/export/hr.xlsx?${query.toString()}`;
}

export interface DriveUpload {
  id: number;
  fileName: string;
  folderName: string;
  webViewLink: string;
  sizeBytes: number;
  createdAt: string;
  createdBy: string | null;
}

export interface DriveState {
  /** Ключ сервисного аккаунта и диск заданы в окружении. */
  configured: boolean;
  /** Имя папки месяца на диске: `2026-09`. */
  folder: string | null;
  uploads: DriveUpload[];
}

/**
 * Подключён ли Google Диск и что уже положили за этот период.
 *
 * Спрашивается до показа кнопки: пока Диск не настроен, обещать действие,
 * которого не будет, нельзя.
 */
export function fetchDriveState(period: Period, signal?: AbortSignal): Promise<DriveState> {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  return request<DriveState>(`/export/drive?${query.toString()}`, signal);
}

/**
 * Положить сверку в папку месяца на Диске. Файл собирает сервер — тем же кодом,
 * что и скачивание, поэтому числа в нём те же, что на экране.
 */
export async function saveHrToDrive(period: Period, dimension: HrDimension): Promise<DriveUpload> {
  const query = new URLSearchParams({ from: period.from, to: period.to, dimension });
  const response = await fetch(`/api/export/hr.xlsx/drive?${query.toString()}`, { method: 'POST' });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: { message?: string } }).error.message ?? response.statusText)
        : response.statusText;
    throw new ApiError(message, response.status);
  }
  return response.json() as Promise<DriveUpload>;
}

export function fetchHrPeople(signal?: AbortSignal): Promise<HrPersonRow[]> {
  return request<HrPersonRow[]>('/hr/people', signal);
}

export function fetchHrCandidates(
  signal?: AbortSignal,
): Promise<{ userKey: string; displayName: string }[]> {
  return request<{ userKey: string; displayName: string }[]>('/hr/candidates', signal);
}

/**
 * Строка списка команд: что помечено и откуда известно название.
 *
 * Понятия «сервисная» в PeopleForce нет — оттуда приходит обратное знание:
 * какие команды точно бизнесовые («Підрозділ» табеля плюс выгрузка состава).
 * Названия вроде `Finance` есть только в Jira, и решение по ним — за человеком.
 */
export interface ServiceTeamRow {
  name: string;
  service: boolean;
  source: string;
  markedBy: string | null;
  markedAt: string | null;
  /** Часы Jira по этой команде за всю историю — чтобы видеть вес решения. */
  seconds: number;
  /** Встречается как команда на задачах: только тогда пометка на что-то влияет. */
  inJira: boolean;
  /** Есть в выгрузке состава PeopleForce — бизнес-команда. */
  inPfTeams: boolean;
  /** Есть в колонке «Підрозділ» табеля — компания холдинга. */
  inPfUnit: boolean;
  /** Есть в колонке «Департамент» табеля: список смешанный, это лишь происхождение. */
  inPfDepartment: boolean;
}

export async function fetchServiceTeams(signal?: AbortSignal): Promise<ServiceTeamRow[]> {
  return request<ServiceTeamRow[]>('/api/hr/service-teams', signal);
}

export async function setServiceTeam(
  name: string,
  service: boolean,
  source: string,
): Promise<void> {
  const response = await fetch('/api/hr/service-teams', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name, service, source }),
  });
  if (!response.ok) throw new ApiError('не удалось сохранить пометку', response.status);
}

export async function linkHrPerson(personId: string, userKey: string | null): Promise<void> {
  const response = await fetch('/api/hr/link', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ personId, userKey }),
  });
  if (!response.ok) throw new ApiError('не удалось сохранить связку', response.status);
}
